import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

// =====================================================
// SKILL TREE DATA STRUCTURE (Fallback/Default Data)
// =====================================================
const skillTreeData = {
  'creative-studio': {
    id: 'creative-studio',
    name: 'Creative Studio',
    icon: '🎨',
    accent: 'from-sky-500 to-purple-500',
    description: 'Master the art of content creation and visual storytelling',
    branches: [
      {
        id: 'sonic-architect',
        name: 'Sonic Architect',
        icon: '🎵',
        skills: [
          { id: 'sa-1', name: 'Audio Basics', xp: 100, level: 1, description: 'Learn fundamental audio editing', unlocks: ['sa-2', 'sa-3'] },
          { id: 'sa-2', name: 'Noise Reduction', xp: 250, level: 2, description: 'Master artifact removal', requires: ['sa-1'], unlocks: ['sa-4'] },
          { id: 'sa-3', name: 'Voice Enhancement', xp: 250, level: 2, description: 'Optimize vocal clarity', requires: ['sa-1'], unlocks: ['sa-4'] },
          { id: 'sa-4', name: 'Master Mixer', xp: 500, level: 3, description: 'Professional mixing techniques', requires: ['sa-2', 'sa-3'], unlocks: ['sa-5'] },
          { id: 'sa-5', name: 'Sonic Legend', xp: 1000, level: 4, description: 'Achieve audio perfection', requires: ['sa-4'], unlocks: [], legendary: true }
        ]
      },
      {
        id: 'visual-alchemist',
        name: 'Visual Alchemist',
        icon: '✨',
        skills: [
          { id: 'va-1', name: 'Color Theory', xp: 100, level: 1, description: 'Understand color grading basics', unlocks: ['va-2', 'va-3'] },
          { id: 'va-2', name: 'HDR Grading', xp: 300, level: 2, description: 'High dynamic range mastery', requires: ['va-1'], unlocks: ['va-4'] },
          { id: 'va-3', name: 'Motion Graphics', xp: 300, level: 2, description: 'Animate your vision', requires: ['va-1'], unlocks: ['va-4'] },
          { id: 'va-4', name: 'Visual Mastery', xp: 600, level: 3, description: 'Create stunning visuals', requires: ['va-2', 'va-3'], unlocks: ['va-5'] },
          { id: 'va-5', name: 'Chrome Wizard', xp: 1200, level: 4, description: 'Transform any footage', requires: ['va-4'], unlocks: [], legendary: true }
        ]
      },
      {
        id: 'narrative-weaver',
        name: 'Narrative Weaver',
        icon: '📖',
        skills: [
          { id: 'nw-1', name: 'Story Structure', xp: 100, level: 1, description: 'Build compelling narratives', unlocks: ['nw-2'] },
          { id: 'nw-2', name: 'Hook Master', xp: 200, level: 2, description: 'Capture attention instantly', requires: ['nw-1'], unlocks: ['nw-3'] },
          { id: 'nw-3', name: 'Retention King', xp: 400, level: 3, description: 'Keep viewers engaged', requires: ['nw-2'], unlocks: ['nw-4'] },
          { id: 'nw-4', name: 'Epic Storyteller', xp: 800, level: 4, description: 'Create unforgettable content', requires: ['nw-3'], unlocks: [], legendary: true }
        ]
      }
    ]
  },
  'proof-of-work': {
    id: 'proof-of-work',
    name: 'Proof of Work',
    icon: '💪',
    accent: 'from-emerald-500 to-lime-400',
    description: 'Demonstrate real-world skills and verified achievements',
    branches: [
      {
        id: 'iron-titan',
        name: 'Iron Titan',
        icon: '🏋️',
        skills: [
          { id: 'it-1', name: 'Fitness Starter', xp: 50, level: 1, description: 'Begin your fitness journey', unlocks: ['it-2', 'it-3'] },
          { id: 'it-2', name: 'Endurance Pro', xp: 200, level: 2, description: 'Build lasting stamina', requires: ['it-1'], unlocks: ['it-4'] },
          { id: 'it-3', name: 'Strength Builder', xp: 200, level: 2, description: 'Develop raw power', requires: ['it-1'], unlocks: ['it-4'] },
          { id: 'it-4', name: 'Peak Performance', xp: 500, level: 3, description: 'Achieve athletic excellence', requires: ['it-2', 'it-3'], unlocks: ['it-5'] },
          { id: 'it-5', name: 'Iron Legend', xp: 1000, level: 4, description: 'Become unstoppable', requires: ['it-4'], unlocks: [], legendary: true }
        ]
      },
      {
        id: 'polymath',
        name: 'Polymath',
        icon: '🧠',
        skills: [
          { id: 'pm-1', name: 'Problem Solver', xp: 100, level: 1, description: 'Solve basic challenges', unlocks: ['pm-2', 'pm-3'] },
          { id: 'pm-2', name: 'Code Wizard', xp: 300, level: 2, description: 'Master programming', requires: ['pm-1'], unlocks: ['pm-4'] },
          { id: 'pm-3', name: 'Math Prodigy', xp: 300, level: 2, description: 'Excel at mathematics', requires: ['pm-1'], unlocks: ['pm-4'] },
          { id: 'pm-4', name: 'Renaissance Mind', xp: 700, level: 3, description: 'Master multiple disciplines', requires: ['pm-2', 'pm-3'], unlocks: [], legendary: true }
        ]
      }
    ]
  },
  'enterprise': {
    id: 'enterprise',
    name: 'Enterprise & Strategy',
    icon: '🏢',
    accent: 'from-amber-500 to-rose-500',
    description: 'Lead teams, build systems, and drive growth',
    branches: [
      {
        id: 'operator',
        name: 'Operator',
        icon: '⚙️',
        skills: [
          { id: 'op-1', name: 'Team Player', xp: 100, level: 1, description: 'Collaborate effectively', unlocks: ['op-2', 'op-3'] },
          { id: 'op-2', name: 'Project Lead', xp: 250, level: 2, description: 'Lead small initiatives', requires: ['op-1'], unlocks: ['op-4'] },
          { id: 'op-3', name: 'Mediator', xp: 250, level: 2, description: 'Resolve conflicts', requires: ['op-1'], unlocks: ['op-4'] },
          { id: 'op-4', name: 'Director', xp: 600, level: 3, description: 'Manage complex operations', requires: ['op-2', 'op-3'], unlocks: ['op-5'] },
          { id: 'op-5', name: 'Executive', xp: 1200, level: 4, description: 'Strategic leadership', requires: ['op-4'], unlocks: [], legendary: true }
        ]
      },
      {
        id: 'architect',
        name: 'Architect',
        icon: '🏗️',
        skills: [
          { id: 'ar-1', name: 'Builder Basics', xp: 100, level: 1, description: 'Create simple tools', unlocks: ['ar-2'] },
          { id: 'ar-2', name: 'System Designer', xp: 300, level: 2, description: 'Design scalable systems', requires: ['ar-1'], unlocks: ['ar-3'] },
          { id: 'ar-3', name: 'Platform Creator', xp: 600, level: 3, description: 'Build entire platforms', requires: ['ar-2'], unlocks: ['ar-4'] },
          { id: 'ar-4', name: 'Visionary', xp: 1000, level: 4, description: 'Shape the future', requires: ['ar-3'], unlocks: [], legendary: true }
        ]
      }
    ]
  }
};

// Challenge templates - will rotate based on time
const challengePool = {
  daily: [
    { id: 'd1', title: 'Early Bird', description: 'Upload content before 9 AM', xp: 50, icon: '🌅' },
    { id: 'd2', title: 'Engagement King', description: 'Reply to 5 comments on your videos', xp: 40, icon: '💬' },
    { id: 'd3', title: 'Explorer', description: 'Watch 10 videos from new creators', xp: 35, icon: '🔍' },
    { id: 'd4', title: 'Supporter', description: 'Like 15 videos today', xp: 30, icon: '❤️' },
    { id: 'd5', title: 'Metadata Master', description: 'Flag 3 EXIF anomalies', xp: 60, icon: '🔬' },
    { id: 'd6', title: 'Social Butterfly', description: 'Follow 3 new creators', xp: 25, icon: '🦋' },
    { id: 'd7', title: 'Clip Dropper', description: 'Share a 30s highlight clip', xp: 45, icon: '📹' },
    { id: 'd8', title: 'Community Helper', description: 'Answer a question in forums', xp: 40, icon: '🤝' },
    { id: 'd9', title: 'Streak Keeper', description: 'Maintain your daily login streak', xp: 20, icon: '🔥' },
  ],
  weekly: [
    { id: 'w1', title: 'Signal Relay', description: 'Compile weekly authenticity briefing', xp: 200, icon: '📡' },
    { id: 'w2', title: 'Mentor Mode', description: 'Help 2 new users complete onboarding', xp: 250, icon: '🎓' },
    { id: 'w3', title: 'Content Marathon', description: 'Upload 5 verified videos', xp: 300, icon: '🏃' },
    { id: 'w4', title: 'Viral Tracker', description: 'Identify 3 trending topics early', xp: 180, icon: '📈' },
    { id: 'w5', title: 'Quality Control', description: 'Review 20 videos for authenticity', xp: 220, icon: '✅' },
    { id: 'w6', title: 'Collaboration Star', description: 'Complete a collab with another creator', xp: 280, icon: '⭐' },
  ],
  monthly: [
    { id: 'm1', title: 'Raid Captain', description: 'Lead a cross-community raid with >90% completion', xp: 1000, icon: '🏴‍☠️' },
    { id: 'm2', title: 'Dataset Curator', description: 'Contribute 20 annotated clips to training data', xp: 800, icon: '📊' },
    { id: 'm3', title: 'Growth Hacker', description: 'Grow your followers by 25%', xp: 750, icon: '🚀' },
    { id: 'm4', title: 'Community Builder', description: 'Recruit 10 active members', xp: 900, icon: '🏘️' },
    { id: 'm5', title: 'Consistency King', description: 'Maintain 30-day upload streak', xp: 1200, icon: '👑' },
  ]
};

// =====================================================
// HELPER FUNCTIONS
// =====================================================
const getTimeUntilReset = (type) => {
  const now = new Date();
  let resetTime = new Date();
  
  switch (type) {
    case 'daily':
      resetTime.setUTCHours(24, 0, 0, 0);
      break;
    case 'weekly':
      const daysUntilMonday = (8 - now.getUTCDay()) % 7 || 7;
      resetTime.setUTCDate(now.getUTCDate() + daysUntilMonday);
      resetTime.setUTCHours(0, 0, 0, 0);
      break;
    case 'monthly':
      resetTime = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
      break;
    default:
      break;
  }
  
  return resetTime.getTime() - now.getTime();
};

const formatCountdown = (ms) => {
  if (ms <= 0) return 'Resetting...';
  
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((ms % (1000 * 60)) / 1000);
  
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  return `${minutes}m ${seconds}s`;
};

// Deterministic selection based on date seed
const selectChallenges = (pool, count, seed) => {
  const seededRandom = (max, seedOffset) => {
    const x = Math.sin(seed + seedOffset) * 10000;
    return Math.floor((x - Math.floor(x)) * max);
  };
  
  const shuffled = [...pool].sort((a, b) => {
    const aVal = seededRandom(1000, pool.indexOf(a));
    const bVal = seededRandom(1000, pool.indexOf(b));
    return aVal - bVal;
  });
  
  return shuffled.slice(0, count);
};

// =====================================================
// COMPONENTS
// =====================================================

const CountdownTimer = ({ type }) => {
  const [timeLeft, setTimeLeft] = useState(getTimeUntilReset(type));
  
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(getTimeUntilReset(type));
    }, 1000);
    return () => clearInterval(timer);
  }, [type]);
  
  const colors = {
    daily: 'text-emerald-400',
    weekly: 'text-sky-400',
    monthly: 'text-purple-400'
  };
  
  return (
    <span className={`font-mono text-sm ${colors[type]}`}>
      {formatCountdown(timeLeft)}
    </span>
  );
};

const ChallengeCard = ({ challenge, type, progress = 0 }) => {
  const bgColors = {
    daily: 'border-emerald-500/30 bg-emerald-500/5',
    weekly: 'border-sky-500/30 bg-sky-500/5',
    monthly: 'border-purple-500/30 bg-purple-500/5'
  };
  
  const progressColors = {
    daily: 'bg-emerald-500',
    weekly: 'bg-sky-500',
    monthly: 'bg-purple-500'
  };
  
  const isComplete = progress >= 100;
  
  return (
    <div className={`relative rounded-2xl border p-4 transition-all hover:scale-[1.02] ${bgColors[type]} ${isComplete ? 'opacity-60' : ''}`}>
      {isComplete && (
        <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
          <span className="text-white text-xs">✓</span>
        </div>
      )}
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-xl">
          {challenge.icon}
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-white">{challenge.title}</h4>
          <p className="text-xs text-white/60">{challenge.description}</p>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono text-amber-400">+{challenge.xp} XP</span>
        <div className="flex items-center gap-2">
          <div className="w-20 h-1.5 rounded-full bg-black/40 overflow-hidden">
            <div 
              className={`h-full transition-all ${progressColors[type]}`}
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
          <span className="text-[10px] text-white/40">{progress}%</span>
        </div>
      </div>
    </div>
  );
};

const SkillNode = ({ skill, isUnlocked, isActive, onClick, position }) => {
  const baseClasses = "absolute w-16 h-16 rounded-2xl border-2 flex flex-col items-center justify-center cursor-pointer transition-all duration-300";
  
  let stateClasses = "";
  if (skill.legendary) {
    stateClasses = isUnlocked 
      ? "border-amber-400 bg-gradient-to-br from-amber-500/30 to-orange-500/30 shadow-[0_0_20px_rgba(251,191,36,0.4)]"
      : "border-amber-400/30 bg-amber-500/10 opacity-50";
  } else if (isUnlocked) {
    stateClasses = "border-emerald-400 bg-emerald-500/20 shadow-[0_0_15px_rgba(16,185,129,0.3)]";
  } else if (isActive) {
    stateClasses = "border-blue-400 bg-blue-500/20 hover:border-blue-300 hover:bg-blue-500/30";
  } else {
    stateClasses = "border-white/20 bg-white/5 opacity-40 cursor-not-allowed";
  }
  
  return (
    <div 
      className={`${baseClasses} ${stateClasses}`}
      style={{ left: position.x, top: position.y }}
      onClick={() => isActive && onClick?.(skill)}
      title={skill.name}
    >
      <span className="text-lg">{skill.legendary ? '⭐' : isUnlocked ? '✓' : '○'}</span>
      <span className="text-[9px] text-white/70 text-center px-1 truncate w-full">{skill.name}</span>
      <span className="text-[8px] text-amber-400">L{skill.level}</span>
    </div>
  );
};

const SkillTreeBranch = ({ branch, unlockedSkills, onSkillClick }) => {
  const [expanded, setExpanded] = useState(false);
  
  // Calculate positions for skill tree layout
  const getSkillPosition = (index, total) => {
    const levelGroups = {};
    branch.skills.forEach((s, i) => {
      if (!levelGroups[s.level]) levelGroups[s.level] = [];
      levelGroups[s.level].push({ ...s, originalIndex: i });
    });
    
    const skill = branch.skills[index];
    const levelSkills = levelGroups[skill.level];
    const indexInLevel = levelSkills.findIndex(s => s.id === skill.id);
    const levelWidth = levelSkills.length;
    
    const x = 40 + ((indexInLevel - (levelWidth - 1) / 2) * 80);
    const y = 20 + (skill.level - 1) * 80;
    
    return { x, y };
  };
  
  const isSkillUnlocked = (skillId) => unlockedSkills.includes(skillId);
  const isSkillActive = (skill) => {
    if (isSkillUnlocked(skill.id)) return false;
    if (!skill.requires || skill.requires.length === 0) return true;
    return skill.requires.every(req => isSkillUnlocked(req));
  };
  
  const unlockedCount = branch.skills.filter(s => isSkillUnlocked(s.id)).length;
  const progress = (unlockedCount / branch.skills.length) * 100;
  
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
      <button 
        className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">{branch.icon}</span>
          <div className="text-left">
            <h4 className="font-semibold text-white">{branch.name}</h4>
            <p className="text-xs text-white/50">{unlockedCount} / {branch.skills.length} skills</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-24 h-2 rounded-full bg-black/40 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500" style={{ width: `${progress}%` }} />
          </div>
          <span className="text-white/40 transition-transform" style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0)' }}>
            ▼
          </span>
        </div>
      </button>
      
      {expanded && (
        <div className="p-4 pt-0">
          <div className="relative h-[340px] bg-black/20 rounded-xl overflow-hidden">
            {/* Connection lines */}
            <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 0 }}>
              {branch.skills.map((skill, i) => {
                const pos = getSkillPosition(i, branch.skills.length);
                return (skill.unlocks || []).map(targetId => {
                  const targetIndex = branch.skills.findIndex(s => s.id === targetId);
                  if (targetIndex === -1) return null;
                  const targetPos = getSkillPosition(targetIndex, branch.skills.length);
                  const isConnected = isSkillUnlocked(skill.id);
                  return (
                    <line
                      key={`${skill.id}-${targetId}`}
                      x1={pos.x + 32}
                      y1={pos.y + 64}
                      x2={targetPos.x + 32}
                      y2={targetPos.y}
                      stroke={isConnected ? '#10b981' : '#ffffff20'}
                      strokeWidth={isConnected ? 3 : 2}
                      strokeDasharray={isConnected ? '0' : '5,5'}
                    />
                  );
                });
              })}
            </svg>
            
            {/* Skill nodes */}
            {branch.skills.map((skill, i) => (
              <SkillNode
                key={skill.id}
                skill={skill}
                isUnlocked={isSkillUnlocked(skill.id)}
                isActive={isSkillActive(skill)}
                onClick={onSkillClick}
                position={getSkillPosition(i, branch.skills.length)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ActiveSkillCard = ({ skill, branch, progress = 0, onClaim }) => (
  <div className="rounded-2xl border border-blue-500/30 bg-blue-500/10 p-4">
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center text-2xl">
          {branch.icon}
        </div>
        <div>
          <p className="text-xs text-white/50">{branch.name}</p>
          <h4 className="font-semibold text-white">{skill.name}</h4>
        </div>
      </div>
      <span className="text-xs font-mono text-amber-400">+{skill.xp} XP</span>
    </div>
    <p className="text-sm text-white/60 mb-3">{skill.description}</p>
    <div className="space-y-2">
      <div className="flex justify-between text-xs text-white/40">
        <span>Progress</span>
        <span>{progress}%</span>
      </div>
      <div className="h-2 rounded-full bg-black/40 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all" style={{ width: `${progress}%` }} />
      </div>
      {progress >= 100 && (
        <button 
          onClick={() => onClaim?.(skill)}
          className="w-full mt-2 py-2 rounded-lg bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-400 transition"
        >
          Claim Skill
        </button>
      )}
    </div>
  </div>
);

// =====================================================
// MAIN COMPONENT
// =====================================================
export default function SkillTree() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [gamificationData, setGamificationData] = useState(null);
  const [selectedPillar, setSelectedPillar] = useState('creative-studio');
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [unlockedSkills, setUnlockedSkills] = useState(['sa-1', 'va-1', 'it-1', 'pm-1', 'op-1', 'ar-1', 'nw-1']); // Starter skills
  const [challengeProgress, setChallengeProgress] = useState({});
  
  // Backend data
  const [backendCategories, setBackendCategories] = useState([]);
  const [backendChallenges, setBackendChallenges] = useState([]);
  const [userSubscription, setUserSubscription] = useState(null);
  const [userTreeProgress, setUserTreeProgress] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);

  // Generate seed for challenge selection (changes daily/weekly/monthly)
  const now = new Date();
  const dailySeed = Math.floor(now.getTime() / (1000 * 60 * 60 * 24));
  const weeklySeed = Math.floor(now.getTime() / (1000 * 60 * 60 * 24 * 7));
  const monthlySeed = now.getFullYear() * 12 + now.getMonth();

  // Select challenges deterministically
  const activeChallenges = useMemo(() => ({
    daily: selectChallenges(challengePool.daily, 3, dailySeed),
    weekly: selectChallenges(challengePool.weekly, 3, weeklySeed),
    monthly: selectChallenges(challengePool.monthly, 3, monthlySeed)
  }), [dailySeed, weeklySeed, monthlySeed]);

  // Fetch gamification data and backend skill trees
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      
      try {
        // Fetch XP leaderboard
        try {
          const leaderboardRes = await apiService.getXPLeaderboard({ limit: 10, timeframe: 'weekly' });
          if (leaderboardRes?.leaderboard) {
            setLeaderboard(leaderboardRes.leaderboard);
          }
        } catch (err) {
          console.warn('Leaderboard not available:', err);
        }
        
        // Fetch skill tree categories from backend
        try {
          const categoriesRes = await apiService.getSkillTrees();
          if (categoriesRes?.categories) {
            setBackendCategories(categoriesRes.categories);
          }
        } catch (err) {
          console.warn('Skill tree categories not available:', err);
        }
        
        // Fetch active challenges from backend
        try {
          const challengesRes = await apiService.request('/api/achievements/challenges');
          if (challengesRes?.challenges) {
            setBackendChallenges(challengesRes.challenges);
          }
        } catch (err) {
          console.warn('Challenges not available:', err);
        }
        
        if (isAuthenticated && user?.id) {
          // Fetch user subscription
          try {
            const subRes = await apiService.request('/api/subscriptions/me');
            setUserSubscription(subRes?.subscription);
          } catch (err) {
            console.warn('Subscription not available:', err);
          }
          
          // Fetch user skill tree progress
          try {
            const progressRes = await apiService.getSkillTreeProgress(user.id);
            setUserTreeProgress(progressRes);
          } catch (err) {
            console.warn('Skill tree progress not available:', err);
          }
          
          // Legacy gamification profile
          try {
            const profile = await apiService.getGamificationProfile(user.id);
            setGamificationData(profile);
            
            // Load unlocked skills from profile
            if (profile?.skills) {
              const skillIds = profile.skills.map(s => s.skill_id || s.id).filter(Boolean);
              setUnlockedSkills(prev => [...new Set([...prev, ...skillIds])]);
            }
            
            // Load challenge progress
            if (profile?.challengeProgress) {
              setChallengeProgress(profile.challengeProgress);
            }
          } catch (err) {
            console.warn('Gamification profile not available:', err);
          }
        }
      } catch (err) {
        console.error('Failed to load skill tree data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.id, isAuthenticated]);

  // Get active skills (skills user is currently working on)
  const activeSkills = useMemo(() => {
    const result = [];
    Object.values(skillTreeData).forEach(pillar => {
      pillar.branches.forEach(branch => {
        branch.skills.forEach(skill => {
          if (unlockedSkills.includes(skill.id)) return;
          const canUnlock = !skill.requires || skill.requires.every(req => unlockedSkills.includes(req));
          if (canUnlock) {
            result.push({ skill, branch, pillar });
          }
        });
      });
    });
    return result.slice(0, 4); // Show max 4 active skills
  }, [unlockedSkills]);

  const handleSkillClick = (skill) => {
    setSelectedSkill(skill);
  };

  const handleClaimSkill = async (skill) => {
    setUnlockedSkills(prev => [...prev, skill.id]);
    setSelectedSkill(null);
    
    // Call API to unlock skill
    if (user?.id) {
      try {
        await apiService.awardXP(user.id, skill.xp, `Unlocked skill: ${skill.name}`);
      } catch (err) {
        console.error('Failed to award XP:', err);
      }
    }
  };

  // XP data
  const xpData = gamificationData?.xp || { total_xp: 0, level: 1, next_level_xp: 100 };
  const level = xpData.level || 1;
  const xp = xpData.total_xp || 0;
  const nextLevelXp = xpData.next_level_xp || Math.floor(1000 * Math.pow(1.5, level - 1));
  const progress = nextLevelXp > 0 ? (xp / nextLevelXp) * 100 : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent mx-auto mb-4" />
          <p className="text-white/60">Loading skill trees...</p>
        </div>
      </div>
    );
  }

  const currentPillar = skillTreeData[selectedPillar];

  return (
    <div className="min-h-screen bg-slate-950 text-white pb-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        
        {/* Sleek Leaderboard at Top */}
        <section className="bg-gradient-to-r from-slate-900/80 to-slate-800/50 border border-white/5 rounded-2xl p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-xl">🏆</span>
              <h2 className="text-lg font-bold">Weekly Leaderboard</h2>
            </div>
            <span className="text-xs text-white/40 uppercase tracking-wider">Top XP Earners</span>
          </div>
          
          <div className="flex items-end justify-center gap-2 md:gap-4 mb-4">
            {/* 2nd Place */}
            {(leaderboard[1] || { username: '---', total_xp: 0 }) && (
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center text-lg md:text-xl font-bold border-2 border-slate-400/50 shadow-lg">
                  {(leaderboard[1]?.username || '?').slice(0, 2).toUpperCase()}
                </div>
                <div className="mt-2 text-center">
                  <p className="text-xs text-white/60 truncate max-w-[80px]">{leaderboard[1]?.username || '---'}</p>
                  <p className="text-sm font-mono text-slate-300">{(leaderboard[1]?.total_xp || 0).toLocaleString()}</p>
                </div>
                <div className="h-16 md:h-20 w-16 md:w-20 bg-gradient-to-t from-slate-600/50 to-slate-500/30 rounded-t-lg mt-2 flex items-end justify-center pb-1">
                  <span className="text-2xl md:text-3xl">🥈</span>
                </div>
              </div>
            )}
            
            {/* 1st Place */}
            {(leaderboard[0] || { username: '---', total_xp: 0 }) && (
              <div className="flex flex-col items-center -mt-4">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-amber-400 to-yellow-600 flex items-center justify-center text-xl md:text-2xl font-bold border-4 border-amber-300/50 shadow-xl shadow-amber-500/20">
                  {(leaderboard[0]?.username || '?').slice(0, 2).toUpperCase()}
                </div>
                <div className="mt-2 text-center">
                  <p className="text-sm font-medium text-amber-300 truncate max-w-[100px]">{leaderboard[0]?.username || '---'}</p>
                  <p className="text-lg font-mono font-bold text-amber-400">{(leaderboard[0]?.total_xp || 0).toLocaleString()}</p>
                </div>
                <div className="h-20 md:h-28 w-20 md:w-24 bg-gradient-to-t from-amber-600/50 to-amber-500/30 rounded-t-lg mt-2 flex items-end justify-center pb-1">
                  <span className="text-3xl md:text-4xl">🥇</span>
                </div>
              </div>
            )}
            
            {/* 3rd Place */}
            {(leaderboard[2] || { username: '---', total_xp: 0 }) && (
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-amber-600 to-amber-800 flex items-center justify-center text-lg md:text-xl font-bold border-2 border-amber-700/50 shadow-lg">
                  {(leaderboard[2]?.username || '?').slice(0, 2).toUpperCase()}
                </div>
                <div className="mt-2 text-center">
                  <p className="text-xs text-white/60 truncate max-w-[80px]">{leaderboard[2]?.username || '---'}</p>
                  <p className="text-sm font-mono text-amber-600">{(leaderboard[2]?.total_xp || 0).toLocaleString()}</p>
                </div>
                <div className="h-12 md:h-16 w-16 md:w-20 bg-gradient-to-t from-amber-800/50 to-amber-700/30 rounded-t-lg mt-2 flex items-end justify-center pb-1">
                  <span className="text-2xl md:text-3xl">🥉</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Remaining leaderboard in a compact row */}
          {leaderboard.length > 3 && (
            <div className="flex flex-wrap justify-center gap-3 pt-3 border-t border-white/5">
              {leaderboard.slice(3, 10).map((user, idx) => (
                <div key={user.user_id || idx} className="flex items-center gap-2 bg-white/5 rounded-full px-3 py-1.5">
                  <span className="text-xs text-white/40 font-mono">#{idx + 4}</span>
                  <span className="text-sm text-white/80">{user.username}</span>
                  <span className="text-xs font-mono text-purple-400">{(user.total_xp || 0).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Header with Level */}
        <header className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-white/40">Progression</p>
            <h1 className="text-2xl md:text-3xl font-black">Skill Trees & Bounties</h1>
          </div>
          <div className="flex items-center gap-4">
            {/* Subscription Badge */}
            {userSubscription && (
              <button
                onClick={() => navigate('/pricing')}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 ${
                  userSubscription.tier_key === 'pro' 
                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' 
                    : userSubscription.tier_key === 'premium'
                      ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                      : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                }`}
              >
                {userSubscription.tier_key === 'pro' ? '👑' : userSubscription.tier_key === 'premium' ? '⭐' : '🎯'}
                {userSubscription.tier_name || userSubscription.tier_key}
              </button>
            )}
            <div className="text-right">
              <span className="text-xl md:text-2xl font-black">Lvl {level}</span>
              <p className="text-xs text-white/50">{xp.toLocaleString()} / {nextLevelXp.toLocaleString()} XP</p>
            </div>
            <div className="w-24 md:w-32 h-3 rounded-full bg-black/40 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
        </header>
        
        {/* Unlocked Trees Progress */}
        {userTreeProgress && (
          <div className="flex items-center gap-4 text-sm text-white/60">
            <span>🌳 {userTreeProgress.total_trees || 0} Skill Trees Unlocked</span>
            {userSubscription?.max_skill_trees !== -1 && (
              <span className="text-white/40">
                ({userTreeProgress.total_trees || 0} / {userSubscription?.max_skill_trees || 1} available)
              </span>
            )}
            {(!userSubscription || userSubscription.tier_key === 'free') && (
              <button
                onClick={() => navigate('/pricing')}
                className="text-purple-400 hover:text-purple-300 transition underline"
              >
                Upgrade for more
              </button>
            )}
          </div>
        )}

        {/* Active Skills Section */}
        {activeSkills.length > 0 && (
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">🎯 Skills In Progress</h2>
              <span className="text-xs text-white/50">Skills you can currently unlock</span>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {activeSkills.map(({ skill, branch }) => (
                <ActiveSkillCard
                  key={skill.id}
                  skill={skill}
                  branch={branch}
                  progress={Math.floor(Math.random() * 80)} // Replace with real progress
                  onClaim={handleClaimSkill}
                />
              ))}
            </div>
          </section>
        )}

        {/* Timed Challenges Section */}
        <section className="space-y-4">
          <h2 className="text-xl font-bold">⏰ Timed Challenges</h2>
          
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Daily Challenges */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-emerald-400">Daily</h3>
                <CountdownTimer type="daily" />
              </div>
              <div className="space-y-3">
                {activeChallenges.daily.map(challenge => (
                  <ChallengeCard 
                    key={challenge.id} 
                    challenge={challenge} 
                    type="daily"
                    progress={challengeProgress[challenge.id] || Math.floor(Math.random() * 100)}
                  />
                ))}
              </div>
            </div>

            {/* Weekly Challenges */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sky-400">Weekly</h3>
                <CountdownTimer type="weekly" />
              </div>
              <div className="space-y-3">
                {activeChallenges.weekly.map(challenge => (
                  <ChallengeCard 
                    key={challenge.id} 
                    challenge={challenge} 
                    type="weekly"
                    progress={challengeProgress[challenge.id] || Math.floor(Math.random() * 70)}
                  />
                ))}
              </div>
            </div>

            {/* Monthly Challenges */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-purple-400">Monthly</h3>
                <CountdownTimer type="monthly" />
              </div>
              <div className="space-y-3">
                {activeChallenges.monthly.map(challenge => (
                  <ChallengeCard 
                    key={challenge.id} 
                    challenge={challenge} 
                    type="monthly"
                    progress={challengeProgress[challenge.id] || Math.floor(Math.random() * 40)}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Skill Tree Pillars */}
        <section className="space-y-4">
          <h2 className="text-xl font-bold">🌳 Skill Trees</h2>
          
          {/* Pillar Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {Object.values(skillTreeData).map(pillar => (
              <button
                key={pillar.id}
                onClick={() => setSelectedPillar(pillar.id)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl whitespace-nowrap transition-all ${
                  selectedPillar === pillar.id
                    ? `bg-gradient-to-r ${pillar.accent} text-white shadow-lg`
                    : 'bg-white/5 text-white/70 hover:bg-white/10'
                }`}
              >
                <span className="text-xl">{pillar.icon}</span>
                <span className="font-medium">{pillar.name}</span>
              </button>
            ))}
          </div>

          {/* Current Pillar Content */}
          {currentPillar && (
            <div className="space-y-4">
              <div className={`p-4 rounded-2xl bg-gradient-to-r ${currentPillar.accent} bg-opacity-10`}>
                <p className="text-white/80">{currentPillar.description}</p>
              </div>
              
              <div className="space-y-4">
                {currentPillar.branches.map(branch => (
                  <SkillTreeBranch
                    key={branch.id}
                    branch={branch}
                    unlockedSkills={unlockedSkills}
                    onSkillClick={handleSkillClick}
                  />
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Skill Detail Modal */}
        {selectedSkill && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div className="max-w-md w-full bg-slate-900 border border-white/10 rounded-3xl p-6 space-y-4 shadow-2xl">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl ${
                    selectedSkill.legendary ? 'bg-amber-500/20' : 'bg-blue-500/20'
                  }`}>
                    {selectedSkill.legendary ? '⭐' : '🎯'}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">{selectedSkill.name}</h3>
                    <p className="text-sm text-white/50">Level {selectedSkill.level} Skill</p>
                  </div>
                </div>
                <button onClick={() => setSelectedSkill(null)} className="text-white/40 hover:text-white text-xl">×</button>
              </div>
              
              <p className="text-white/70">{selectedSkill.description}</p>
              
              <div className="bg-white/5 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">XP Reward</span>
                  <span className="text-amber-400 font-mono">+{selectedSkill.xp} XP</span>
                </div>
                {selectedSkill.requires && (
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">Requires</span>
                    <span className="text-white/70">{selectedSkill.requires.length} prerequisite(s)</span>
                  </div>
                )}
                {selectedSkill.unlocks && selectedSkill.unlocks.length > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">Unlocks</span>
                    <span className="text-emerald-400">{selectedSkill.unlocks.length} new skill(s)</span>
                  </div>
                )}
              </div>
              
              <button
                onClick={() => setSelectedSkill(null)}
                className="w-full py-3 rounded-xl bg-blue-500 hover:bg-blue-400 text-white font-semibold transition"
              >
                Start Working on This Skill
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
